import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Pause, Play } from 'lucide-react';
import { episodeNumbers, formatPostDate, isPodcast, postPath, usePostBySlug } from '../api/blog';
import PodcastPlayer, { useAudioPlayer } from './PodcastPlayer';
import { DetailNotFound, DetailLoading } from './BlogDetailPage';
import { podcastMeta, usePageMeta } from '../seo/seo';

const PodcastDetailPage = () => {
  const location = useLocation();
  const { slug } = useParams();
  const { post, posts, status, reload } = usePostBySlug(slug, location.state?.post);
  const player = useAudioPlayer();
  usePageMeta(post ? podcastMeta(post) : undefined);

  if (!post) {
    if (status === 'loading') return <DetailLoading />;
    return <DetailNotFound kind="episode" onRetry={status === 'error' ? reload : undefined} />;
  }

  const episodes = episodeNumbers(posts);
  const episode = episodes.get(post.id);
  const moreEpisodes = posts.filter((p) => isPodcast(p) && p.id !== post.id).slice(0, 4);

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="bg-gradient-to-r from-brandPurple to-brandBlue">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-4">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> All episodes
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <div className="grid gap-8 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] items-center">
          <img
            src={post.content.image_url}
            alt=""
            className="w-full rounded-2xl shadow-lg object-cover bg-gray-100 dark:bg-gray-800"
          />
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#3399FF]/10 text-[#3399FF]">
                Podcast{episode ? ` · Episode ${episode}` : ''}
              </span>
              {formatPostDate(post) && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {formatPostDate(post)}
                </span>
              )}
            </div>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight">{post.title}</h1>
            <div className="mt-8 p-6 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <PodcastPlayer player={player} audioUrl={post.content.audio_url!} />
            </div>
          </div>
        </div>

        {post.content.blog_text && (
          <div className="mt-12 max-w-3xl space-y-4 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            {post.content.blog_text.split(/\n\s*\n/).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        )}

        {moreEpisodes.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">More Episodes</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {moreEpisodes.map((other) => {
                const isCurrent = player.src === other.content.audio_url;
                const isPlaying = isCurrent && player.isPlaying;
                return (
                  <div
                    key={other.id}
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-[#36AC98] transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => (isCurrent ? player.toggle() : player.play(other.content.audio_url!))}
                      className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-full bg-[#3399FF]/10 text-[#3399FF] hover:bg-brandPurple hover:text-white transition-colors"
                      aria-label={`${isPlaying ? 'Pause' : 'Play'} ${other.title}`}
                    >
                      {isPlaying ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
                    </button>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Episode {episodes.get(other.id)} · {formatPostDate(other)}
                      </p>
                      <Link
                        to={postPath(other)}
                        state={{ post: other }}
                        className="block font-semibold text-gray-900 dark:text-white hover:text-brandPurple dark:hover:text-brandBlue transition-colors"
                      >
                        {other.title}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PodcastDetailPage;
